/**
 * @file 框架-左侧导航栏
 * @description 提供菜单导航、搜索、收起/展开等功能
 * @author 智慧云行
 * @version 1.1.0
 */
<template>
  <div 
    id="frame-menu" 
    :class="{ 'menu-collapsed': isCollapsed }"
    @keydown="handleKeydown"
    tabindex="0">
    
    <!-- 菜单列表 -->
    <div class="menu-box" v-loading="menuLoading" :class="{ 'has-search': searchText }">
      <!-- 加载失败提示 -->
      <div v-if="menuError" class="menu-error">
        <i class="el-icon-warning-outline"></i>
        <p>菜单加载失败</p>
        <el-button type="text" @click="retryLoadMenu">点击重试</el-button>
      </div>
      
      <!-- 搜索无结果提示 -->
      <div v-else-if="searchText && filteredMenu.length === 0" class="menu-empty">
        <i class="el-icon-search"></i>
        <p>未找到匹配的菜单</p>
        <el-button type="text" @click="clearSearch">清除搜索</el-button>
      </div>
      
      <!-- 菜单列表 -->
      <el-menu 
        v-else
        ref="menuRef"
        :default-active="defaultActive" 
        :collapse="isCollapsed"
        :collapse-transition="true"
        @select="menuSelect" 
        :unique-opened="true" 
        background-color="#1d1e23"
        text-color="#ffffffb3" 
        active-text-color="#ffffff">
        <template v-for="item in filteredMenu">
          <el-submenu 
            v-if="item.children && item.children.length > 0" 
            :key="item.id" 
            :index="String(item.id)"
            :popper-class="isCollapsed ? 'collapsed-submenu-popper' : ''">
            <template slot="title">
              <i :class="getMenuIcon(item)"></i>
              <span slot="title">{{ item.menuName }}</span>
            </template>
            <el-menu-item 
              v-for="child in item.children" 
              :key="child.id" 
              :index="String(child.id)"
              :class="{ 'search-highlight': searchText && isMatchSearch(child.menuName) }">
              <i :class="getChildMenuIcon(child, item)"></i>
              <span>{{ child.menuName }}</span>
            </el-menu-item>
          </el-submenu>
          <el-menu-item 
            v-else 
            :key="item.id + 'level'" 
            :index="String(item.id)"
            :class="{ 'search-highlight': searchText && isMatchSearch(item.menuName) }">
            <i :class="getMenuIcon(item)"></i>
            <span slot="title">{{ item.menuName }}</span>
          </el-menu-item>
        </template>
      </el-menu>
    </div>
    
    <!-- 折叠按钮 -->
    <div class="menu-collapse-btn" @click="toggleCollapse">
      <el-tooltip :content="isCollapsed ? '展开菜单' : '收起菜单'" placement="right">
        <div class="collapse-btn-inner">
          <i :class="isCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
          <span v-if="!isCollapsed">收起菜单</span>
        </div>
      </el-tooltip>
    </div>
    
    <!-- 版本号 -->
    <div class="version-info" @click="showVersionDetail">
      <div class="version-badge">
        <i class="el-icon-info"></i>
        <span class="version-text">{{ version }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// 导入版本信息
import versionInfo from '@/version.json'

// 防抖函数
function debounce(fn, delay = 300) {
  let timer = null
  return function(...args) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

export default {
  name: "frame-menu",
  data() {
    return {
      // 默认展开项
      defaultActive: "",
      // 版本信息
      versionInfo: versionInfo,
      version: versionInfo.version || window.configure?.version || 'V1.0.0',
      // 原始菜单数据（不可变）
      originalMenu: [],
      // 搜索文本
      searchText: '',
      // 菜单是否收起
      isCollapsed: false,
      // 菜单加载状态
      menuLoading: false,
      // 菜单加载错误
      menuError: false,
      // 防抖搜索函数
      handleSearchDebounced: null
    };
  },
  
  computed: {
    /**
     * 版本号提示信息
     */
    versionTooltip() {
      const info = this.versionInfo
      return `版本: ${info.version}\n构建时间: ${info.buildTime}\nGit: ${info.gitHash}`
    },
    
    /**
     * 过滤后的菜单（纯函数，不修改原始数据）
     */
    filteredMenu() {
      if (!this.searchText) {
        return this.originalMenu
      }
      
      const searchLower = (this.searchText || '').toLowerCase()
      
      return this.originalMenu
        .map(item => {
          // 检查父菜单是否匹配
          const parentMatch = item.menuName && item.menuName.toLowerCase().includes(searchLower)
          
          // 检查子菜单是否匹配
          let matchedChildren = []
          if (item.children && item.children.length > 0) {
            matchedChildren = item.children.filter(child => 
              child.menuName && child.menuName.toLowerCase().includes(searchLower)
            )
          }
          
          // 父菜单匹配或有匹配的子菜单
          if (parentMatch || matchedChildren.length > 0) {
            return {
              ...item,
              // 父菜单匹配时显示所有子菜单，否则只显示匹配的子菜单
              children: parentMatch ? item.children : matchedChildren
            }
          }
          
          return null
        })
        .filter(Boolean)
    }
  },
  
  watch: {
    $route: {
      deep: true,
      immediate: true,
      handler(val) {
        let active = sessionStorage.getItem("menuActive");
        if (val && val.meta.id != active && val.meta.pid != active) {
          this.defaultActive = String(val.meta.id);
          sessionStorage.setItem("menuActive", val.meta.id);
        }
      },
    },
  },

  created() {
    // 初始化防抖搜索函数
    this.handleSearchDebounced = debounce(this.handleSearch, 200)
    
    this.menuList();
    this.defaultActive = sessionStorage.getItem("menuActive");
    
    // 监听菜单更新事件（菜单管理保存/删除后触发，重新加载菜单）
    this.$bus.$on('menu-updated', this.menuList)
    
    // 恢复菜单收起状态
    const collapsed = localStorage.getItem('menuCollapsed')
    if (collapsed !== null) {
      this.isCollapsed = collapsed === 'true'
    }
    
    // 注册全局快捷键
    document.addEventListener('keydown', this.handleGlobalKeydown)
  },
  
  beforeDestroy() {
    // 移除全局快捷键监听
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    // 取消菜单更新事件监听
    this.$bus.$off('menu-updated', this.menuList)
  },

  methods: {
    /**
     * 显示版本详情
     */
    showVersionDetail() {
      const info = this.versionInfo
      const labels = {
        title: '版本信息',
        version: '版本号',
        fullVersion: '完整版本',
        buildTime: '构建时间',
        buildDate: '构建日期',
        gitHash: 'Git Hash',
        gitBranch: 'Git 分支',
        env: '构建环境',
        confirm: '确定'
      }
      const items = [
        { label: labels.version, value: info.version },
        { label: labels.fullVersion, value: info.fullVersion },
        { label: labels.buildTime, value: info.buildTime },
        { label: labels.buildDate, value: info.buildDate },
        { label: labels.gitHash, value: info.gitHash },
        { label: labels.gitBranch, value: info.gitBranch },
        { label: labels.env, value: info.env }
      ]
      const content = items.map(item => `<p><strong>${item.label}：</strong>${item.value}</p>`).join('')
      this.$alert(
        `<div style="line-height: 2;">${content}</div>`,
        labels.title,
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: labels.confirm,
          customClass: 'version-dialog'
        }
      )
    },
    
    /**
     * 全局快捷键处理
     */
    handleGlobalKeydown(e) {
      // Ctrl+K 聚焦搜索框
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault()
        this.focusSearch()
      }
      // Ctrl+B 切换菜单收起/展开
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault()
        this.toggleCollapse()
      }
    },
    
    /**
     * 菜单内键盘导航
     */
    handleKeydown(e) {
      // 上下箭头导航由 el-menu 组件处理
      // 这里可以扩展其他快捷键
    },
    
    /**
     * 聚焦搜索框
     */
    focusSearch() {
      if (this.isCollapsed) {
        this.isCollapsed = false
        localStorage.setItem('menuCollapsed', 'false')
      }
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus()
      })
    },
    
    /**
     * 搜索菜单（实际搜索逻辑由 computed 处理）
     */
    handleSearch() {
      // 搜索逻辑已移至 computed: filteredMenu
      // 此方法保留用于防抖回调
    },
    
    /**
     * 清除搜索
     */
    clearSearch() {
      this.searchText = ''
    },
    
    /**
     * 检查菜单名是否匹配搜索
     */
    isMatchSearch(menuName) {
      if (!this.searchText || !menuName) return false
      return menuName.toLowerCase().includes((this.searchText || '').toLowerCase())
    },
    
    /**
     * 聚焦第一个菜单项
     */
    focusFirstMenuItem() {
      this.$nextTick(() => {
        const firstItem = this.$el.querySelector('.el-menu-item')
        if (firstItem) {
          firstItem.focus()
        }
      })
    },
    
    /**
     * 切换菜单收起/展开
     */
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed
      localStorage.setItem('menuCollapsed', this.isCollapsed)
      
      // 收起时清除搜索
      if (this.isCollapsed) {
        this.searchText = ''
      }
    },
    
    /**
     * 重试加载菜单
     */
    retryLoadMenu() {
      this.menuError = false
      this.menuList()
    },
    
    /**
     * 获取菜单信息列表
     */
    async menuList() {
      this.menuLoading = true
      this.menuError = false
      
      try {
        let url = "/api/v1/manage/menu/permission";
        let data = await this.$axios.get(url);
        
        if (data && data.code == 1) {
          // 使用 Object.freeze 冻结原始数据，防止意外修改
          this.originalMenu = Object.freeze(JSON.parse(JSON.stringify(data.data.menu || [])))
          
          this.$store.dispatch("acPermissions", data.data.permissions);
          this.$store.dispatch("acButtons", data.data.buttons);
          sessionStorage.setItem("permissions", data.data.permissions);
          
          // 处理首页跳转逻辑
          if (this.$route.path == "/frame") {
            this.handleInitialRoute(data.data.permissions)
          }
        } else {
          this.menuError = true
          this.$message.error('菜单加载失败，请重试')
        }
      } catch (error) {
        console.error('菜单加载异常:', error)
        this.menuError = true
        this.$message.error('菜单加载异常，请检查网络连接')
      } finally {
        this.menuLoading = false
      }
    },
    
    /**
     * 处理初始路由跳转
     */
    handleInitialRoute(permissions) {
      const permissionList = permissions.split(",").map(item => Number(item))
      const hasDispatchPermission = permissionList.includes(24)
      
      if (hasDispatchPermission) {
        this.$router.replace("/dispatchCustomize")
        sessionStorage.setItem("menuActiveUrl", "/dispatchCustomize")
      } else if (this.originalMenu.length > 0) {
        // 获取第一个可用路由
        const firstMenu = this.originalMenu[0]
        let path = firstMenu.path
        
        if (firstMenu.children && firstMenu.children.length > 0) {
          path = firstMenu.children[0].path
        }
        
        sessionStorage.setItem("menuActiveUrl", path)
        this.$router.replace(path)
      }
    },

    /**
     * 获取一级菜单图标
     * 支持Element UI图标和iconfont图标，为没有图标的菜单提供默认映射
     */
    getMenuIcon(item) {
      // 一级菜单默认图标映射（根据菜单名称或ID）
      // 优先使用此映射，确保所有菜单都有正确的图标
      const menuIconMap = {
        // 按菜单名称映射
        '工作台': 'el-icon-s-home',
        '首页': 'el-icon-s-home',
        '订单管理': 'el-icon-s-order',
        '工单管理': 'el-icon-document-copy',
        '资源管理': 'el-icon-s-cooperation',
        '运营中心': 'el-icon-s-operation',
        '排班管理': 'el-icon-date',
        '财务中心': 'el-icon-s-finance',
        '业务管理': 'el-icon-s-management',
        '营销管理': 'el-icon-s-marketing',
        '数据分析': 'el-icon-data-analysis',
        '统计分析': 'el-icon-s-data',
        '渠道管理': 'el-icon-share',
        '系统管理': 'el-icon-s-tools',
        '场站业务': 'el-icon-office-building',
        '文旅业务': 'el-icon-location-outline',
        '票据管理': 'el-icon-tickets',
        '推荐线路': 'el-icon-guide',
      }
      
      // 优先按名称匹配（确保前端定义的图标优先级最高）
      if (menuIconMap[item.menuName]) {
        return menuIconMap[item.menuName]
      }
      
      // 如果菜单有图标配置，判断是Element UI图标还是iconfont
      if (item.icon) {
        // Element UI图标以 el-icon- 开头
        if (item.icon.startsWith('el-icon-')) {
          return item.icon
        }
        // iconfont图标（需要项目中有对应字体文件才能显示）
        // 检查是否是已知的有效iconfont图标
        const validIconfontIcons = [
          'icon-order', 'icon-car', 'icon-driver', 'icon-line', 'icon-station',
          'icon-finance', 'icon-marketing', 'icon-system', 'icon-data'
        ]
        if (validIconfontIcons.includes(item.icon)) {
          return `iconfont ${item.icon}`
        }
        // 未知的iconfont图标，回退到默认
      }
      
      // 默认图标
      return 'el-icon-menu'
    },

    /**
     * 获取二级菜单图标
     * 根据菜单ID和索引确保每个菜单都有不同的图标
     */
    getChildMenuIcon(child, parent) {
      // 如果子菜单有自定义图标，优先使用
      if (child.icon) {
        // Element UI图标以 el-icon- 开头
        if (child.icon.startsWith('el-icon-')) {
          return child.icon
        }
        return `iconfont ${child.icon}`
      }
      
      const name = child.menuName || ''
      
      // 根据菜单名称关键词匹配图标（优先级最高）
      const keywordIconMap = {
        '订单': 'el-icon-tickets',
        '工单': 'el-icon-document-copy',
        '统计': 'el-icon-data-analysis',
        '报表': 'el-icon-data-board',
        '分析': 'el-icon-pie-chart',
        '汇总': 'el-icon-s-data',
        '配置': 'el-icon-setting',
        '设置': 'el-icon-s-tools',
        '参数': 'el-icon-s-operation',
        '审核': 'el-icon-finished',
        '审批': 'el-icon-circle-check',
        '复核': 'el-icon-success',
        '用户': 'el-icon-user',
        '司机': 'el-icon-s-custom',
        '乘客': 'el-icon-user-solid',
        '会员': 'el-icon-star-on',
        '人员': 'el-icon-s-check',
        '车辆': 'el-icon-truck',
        '车型': 'el-icon-bicycle',
        '线路': 'el-icon-position',
        '路线': 'el-icon-guide',
        '站点': 'el-icon-place',
        '财务': 'el-icon-money',
        '账单': 'el-icon-wallet',
        '结算': 'el-icon-coin',
        '收入': 'el-icon-sell',
        '支出': 'el-icon-sold-out',
        '充值': 'el-icon-bank-card',
        '消息': 'el-icon-message',
        '通知': 'el-icon-bell',
        '短信': 'el-icon-chat-line-square',
        '推送': 'el-icon-promotion',
        '日志': 'el-icon-notebook-2',
        '操作': 'el-icon-mouse',
        '历史': 'el-icon-time',
        '权限': 'el-icon-lock',
        '角色': 'el-icon-s-custom',
        '账号': 'el-icon-key',
        '地图': 'el-icon-map-location',
        '定位': 'el-icon-location-outline',
        '轨迹': 'el-icon-aim',
        '调度': 'el-icon-s-grid',
        '排班': 'el-icon-date',
        '班次': 'el-icon-alarm-clock',
        '评价': 'el-icon-chat-dot-round',
        '反馈': 'el-icon-chat-line-round',
        '投诉': 'el-icon-warning-outline',
        '导入': 'el-icon-upload2',
        '导出': 'el-icon-download',
        '下载': 'el-icon-folder-opened',
        '预约': 'el-icon-edit-outline',
        '预订': 'el-icon-document-add',
        '预定': 'el-icon-document-checked',
        '优惠': 'el-icon-present',
        '活动': 'el-icon-s-flag',
        '促销': 'el-icon-goods',
        '券': 'el-icon-collection-tag',
        '渠道': 'el-icon-connection',
        '合作': 'el-icon-share',
        '代理': 'el-icon-s-promotion',
        '机构': 'el-icon-office-building',
        '组织': 'el-icon-school',
        '公司': 'el-icon-suitcase',
        '企业': 'el-icon-s-home',
        '列表': 'el-icon-document',
        '管理': 'el-icon-s-management',
        '记录': 'el-icon-notebook-1',
        '明细': 'el-icon-document-copy',
        '详情': 'el-icon-view',
        '查询': 'el-icon-search',
        '搜索': 'el-icon-zoom-in',
        '新增': 'el-icon-plus',
        '添加': 'el-icon-circle-plus-outline',
        '编辑': 'el-icon-edit',
        '修改': 'el-icon-edit-outline',
        '删除': 'el-icon-delete',
        '导航': 'el-icon-discover',
        '首页': 'el-icon-s-home',
        '看板': 'el-icon-data-line',
        '监控': 'el-icon-video-camera',
        '实时': 'el-icon-video-play',
        '预警': 'el-icon-warning',
        '告警': 'el-icon-message-solid',
        '异常': 'el-icon-error',
        '正常': 'el-icon-success',
        '待处理': 'el-icon-loading',
        '已完成': 'el-icon-circle-check',
        '进行中': 'el-icon-loading',
        '退款': 'el-icon-refresh-left',
        '退票': 'el-icon-back',
        '改签': 'el-icon-sort',
        '取消': 'el-icon-close',
        '确认': 'el-icon-check',
        '发送': 'el-icon-s-promotion',
        '接收': 'el-icon-receiving',
        '上传': 'el-icon-upload',
        '图片': 'el-icon-picture-outline',
        '文件': 'el-icon-folder',
        '附件': 'el-icon-paperclip',
        '打印': 'el-icon-printer',
        '复制': 'el-icon-copy-document',
        '分享': 'el-icon-share',
        '收藏': 'el-icon-star-off',
        '标记': 'el-icon-collection-tag',
        '备注': 'el-icon-edit',
        '说明': 'el-icon-info',
        '帮助': 'el-icon-question',
        '关于': 'el-icon-info',
        '版本': 'el-icon-s-release',
        '更新': 'el-icon-refresh',
        '同步': 'el-icon-refresh-right',
        '刷新': 'el-icon-refresh',
        '重置': 'el-icon-refresh-left',
        '清空': 'el-icon-delete-solid',
        '全部': 'el-icon-s-unfold',
        '筛选': 'el-icon-s-operation',
        '排序': 'el-icon-sort',
        '分类': 'el-icon-menu',
        '类型': 'el-icon-s-grid',
        '状态': 'el-icon-s-flag',
        '等级': 'el-icon-medal',
        '积分': 'el-icon-trophy',
        '余额': 'el-icon-wallet',
        '押金': 'el-icon-coin',
        '保证金': 'el-icon-money',
        '佣金': 'el-icon-present',
        '提现': 'el-icon-sold-out',
        '转账': 'el-icon-right',
        '支付': 'el-icon-shopping-cart-2',
        '收款': 'el-icon-receiving',
        '发票': 'el-icon-tickets',
        '合同': 'el-icon-document-checked',
        '协议': 'el-icon-document',
        '证书': 'el-icon-postcard',
        '资质': 'el-icon-medal-1',
        '认证': 'el-icon-success',
        '验证': 'el-icon-circle-check',
        '绑定': 'el-icon-link',
        '解绑': 'el-icon-connection',
        '启用': 'el-icon-open',
        '禁用': 'el-icon-turn-off',
        '锁定': 'el-icon-lock',
        '解锁': 'el-icon-unlock',
        '冻结': 'el-icon-cold-drink',
        '解冻': 'el-icon-hot-water',
        '上架': 'el-icon-top',
        '下架': 'el-icon-bottom',
        '发布': 'el-icon-s-promotion',
        '撤回': 'el-icon-refresh-left',
        '归档': 'el-icon-folder-checked',
        '恢复': 'el-icon-refresh-right',
        '备份': 'el-icon-folder-add',
        '还原': 'el-icon-folder-remove',
        '迁移': 'el-icon-d-arrow-right',
        '转移': 'el-icon-right',
        '分配': 'el-icon-share',
        '指派': 'el-icon-s-custom',
        '接单': 'el-icon-receiving',
        '派单': 'el-icon-s-promotion',
        '抢单': 'el-icon-aim',
        '完成': 'el-icon-success',
        '开始': 'el-icon-video-play',
        '结束': 'el-icon-video-pause',
        '暂停': 'el-icon-video-pause',
        '继续': 'el-icon-video-play',
        '终止': 'el-icon-switch-button',
        '关闭': 'el-icon-close',
        '打开': 'el-icon-folder-opened',
        '展开': 'el-icon-arrow-down',
        '收起': 'el-icon-arrow-up',
        '放大': 'el-icon-zoom-in',
        '缩小': 'el-icon-zoom-out',
        '全屏': 'el-icon-full-screen',
        '退出': 'el-icon-switch-button',
        '登录': 'el-icon-user',
        '注册': 'el-icon-user-solid',
        '注销': 'el-icon-switch-button',
        '密码': 'el-icon-lock',
        '安全': 'el-icon-lock',
        '隐私': 'el-icon-view',
        '通用': 'el-icon-s-tools',
        '高级': 'el-icon-s-operation',
        '基础': 'el-icon-s-grid',
        '扩展': 'el-icon-s-unfold',
        '插件': 'el-icon-s-grid',
        '模块': 'el-icon-menu',
        '组件': 'el-icon-s-grid',
        '模板': 'el-icon-document-copy',
        '主题': 'el-icon-brush',
        '样式': 'el-icon-magic-stick',
        '布局': 'el-icon-s-grid',
        '界面': 'el-icon-monitor',
        '窗口': 'el-icon-copy-document',
        '弹窗': 'el-icon-chat-square',
        '提示': 'el-icon-info',
        '确认框': 'el-icon-question',
        '加载': 'el-icon-loading',
        '进度': 'el-icon-loading',
        '步骤': 'el-icon-s-operation',
        '流程': 'el-icon-s-operation',
        '审批流': 'el-icon-s-check',
        '工作流': 'el-icon-s-operation',
        '任务': 'el-icon-s-claim',
        '待办': 'el-icon-s-claim',
        '已办': 'el-icon-s-check',
        '草稿': 'el-icon-edit',
        '回收站': 'el-icon-delete',
        '垃圾箱': 'el-icon-delete-solid',
        '收件箱': 'el-icon-message',
        '发件箱': 'el-icon-s-promotion',
        '已读': 'el-icon-view',
        '未读': 'el-icon-chat-dot-square',
        '标星': 'el-icon-star-on',
        '置顶': 'el-icon-top',
        '热门': 'el-icon-s-flag',
        '推荐': 'el-icon-s-opportunity',
        '精选': 'el-icon-star-on',
        '最新': 'el-icon-time',
        '最热': 'el-icon-s-flag',
        '排行': 'el-icon-s-data',
        '榜单': 'el-icon-trophy',
        '商品': 'el-icon-goods',
        '产品': 'el-icon-box',
        '服务': 'el-icon-service',
        '套餐': 'el-icon-s-goods',
        '规格': 'el-icon-s-grid',
        '属性': 'el-icon-s-operation',
        '库存': 'el-icon-s-goods',
        '入库': 'el-icon-bottom',
        '出库': 'el-icon-top',
        '盘点': 'el-icon-s-claim',
        '调拨': 'el-icon-sort',
        '采购': 'el-icon-shopping-cart-1',
        '销售': 'el-icon-sell',
        '退货': 'el-icon-refresh-left',
        '换货': 'el-icon-sort',
        '维修': 'el-icon-s-tools',
        '保养': 'el-icon-s-tools',
        '检测': 'el-icon-s-check',
        '检查': 'el-icon-view',
        '巡检': 'el-icon-aim',
        '考勤': 'el-icon-date',
        '打卡': 'el-icon-time',
        '签到': 'el-icon-location',
        '签退': 'el-icon-location-outline',
        '请假': 'el-icon-document-remove',
        '加班': 'el-icon-time',
        '出差': 'el-icon-suitcase',
        '报销': 'el-icon-wallet',
        '申请': 'el-icon-document-add',
        '审批单': 'el-icon-document-checked',
        '表单': 'el-icon-document',
        '问卷': 'el-icon-s-claim',
        '调查': 'el-icon-s-data',
        '投票': 'el-icon-s-check',
        '抽奖': 'el-icon-present',
        '红包': 'el-icon-present',
        '奖励': 'el-icon-trophy',
        '惩罚': 'el-icon-warning',
        '扣款': 'el-icon-sold-out',
        '罚款': 'el-icon-warning-outline',
        '赔偿': 'el-icon-money',
        '理赔': 'el-icon-document-checked',
        '保险': 'el-icon-s-check',
        '担保': 'el-icon-s-check',
        '抵押': 'el-icon-lock',
        '贷款': 'el-icon-money',
        '还款': 'el-icon-refresh-left',
        '分期': 'el-icon-s-grid',
        '利息': 'el-icon-coin',
        '手续费': 'el-icon-coin',
        '税费': 'el-icon-coin',
        '折扣': 'el-icon-discount',
        '满减': 'el-icon-minus',
        '包邮': 'el-icon-truck',
        '运费': 'el-icon-truck',
        '物流': 'el-icon-truck',
        '快递': 'el-icon-box',
        '配送': 'el-icon-truck',
        '自提': 'el-icon-location',
        '门店': 'el-icon-s-shop',
        '仓库': 'el-icon-s-home',
        '供应商': 'el-icon-s-custom',
        '客户': 'el-icon-user',
        '联系人': 'el-icon-phone-outline',
        '电话': 'el-icon-phone',
        '手机': 'el-icon-mobile-phone',
        '邮箱': 'el-icon-message',
        '地址': 'el-icon-location-information',
        '区域': 'el-icon-place',
        '城市': 'el-icon-office-building',
        '省份': 'el-icon-map-location',
        '国家': 'el-icon-s-flag',
        '语言': 'el-icon-s-comment',
        '货币': 'el-icon-money',
        '汇率': 'el-icon-sort',
        '时区': 'el-icon-time',
        '日期': 'el-icon-date',
        '时间': 'el-icon-time',
        '周期': 'el-icon-refresh',
        '频率': 'el-icon-s-data',
        '次数': 'el-icon-s-data',
        '数量': 'el-icon-s-data',
        '金额': 'el-icon-money',
        '比例': 'el-icon-pie-chart',
        '百分比': 'el-icon-pie-chart',
        '增长': 'el-icon-top-right',
        '下降': 'el-icon-bottom-left',
        '趋势': 'el-icon-data-line',
        '对比': 'el-icon-s-data',
        '环比': 'el-icon-refresh',
        '同比': 'el-icon-s-data',
        '占比': 'el-icon-pie-chart',
        '分布': 'el-icon-s-grid',
        '热力图': 'el-icon-s-data',
        '柱状图': 'el-icon-s-data',
        '折线图': 'el-icon-data-line',
        '饼图': 'el-icon-pie-chart',
        '仪表盘': 'el-icon-odometer',
        '大屏': 'el-icon-monitor',
        '可视化': 'el-icon-data-board',
        '报告': 'el-icon-document',
        '总结': 'el-icon-s-claim',
        '概览': 'el-icon-view',
        '摘要': 'el-icon-document',
        '索引': 'el-icon-s-grid',
        '目录': 'el-icon-folder',
        '章节': 'el-icon-document',
        '段落': 'el-icon-document',
        '标题': 'el-icon-s-flag',
        '正文': 'el-icon-document',
        '附录': 'el-icon-paperclip',
        '参考': 'el-icon-link',
        '引用': 'el-icon-link',
        '注释': 'el-icon-chat-line-square',
        '脚注': 'el-icon-document',
        '尾注': 'el-icon-document',
        '书签': 'el-icon-collection-tag',
        '标签': 'el-icon-price-tag',
        '分组': 'el-icon-s-grid',
        '合并': 'el-icon-s-unfold',
        '拆分': 'el-icon-s-fold',
        '关联': 'el-icon-link',
        '解除': 'el-icon-connection',
        '依赖': 'el-icon-link',
        '继承': 'el-icon-bottom',
        '重写': 'el-icon-edit',
        '覆盖': 'el-icon-document-copy',
        '替换': 'el-icon-sort',
        '插入': 'el-icon-plus',
        '追加': 'el-icon-circle-plus',
        '移除': 'el-icon-minus',
        '清除': 'el-icon-delete',
        '格式化': 'el-icon-s-tools',
        '压缩': 'el-icon-s-fold',
        '解压': 'el-icon-s-unfold',
        '加密': 'el-icon-lock',
        '解密': 'el-icon-unlock',
        '签名': 'el-icon-edit',
        '验签': 'el-icon-circle-check',
        '授权': 'el-icon-key',
        '撤销授权': 'el-icon-close',
        '委托': 'el-icon-share',
        '代办': 'el-icon-s-custom',
        '转办': 'el-icon-right',
        '退回': 'el-icon-back',
        '驳回': 'el-icon-close',
        '通过': 'el-icon-check',
        '拒绝': 'el-icon-close',
        '同意': 'el-icon-check',
        '不同意': 'el-icon-close',
        '已读回执': 'el-icon-success',
        '回复': 'el-icon-chat-line-round',
        '转发': 'el-icon-right',
        '抄送': 'el-icon-s-promotion',
        '密送': 'el-icon-lock',
        '群发': 'el-icon-s-promotion',
        '单发': 'el-icon-s-promotion',
        '定时': 'el-icon-alarm-clock',
        '延时': 'el-icon-time',
        '立即': 'el-icon-video-play',
        '手动': 'el-icon-mouse',
        '自动': 'el-icon-s-tools',
        '批量': 'el-icon-s-grid',
        '单个': 'el-icon-document',
        '全选': 'el-icon-finished',
        '反选': 'el-icon-sort',
        '取消选择': 'el-icon-close',
        '多选': 'el-icon-s-grid',
        '单选': 'el-icon-circle-check',
        '必选': 'el-icon-star-on',
        '可选': 'el-icon-star-off',
        '默认': 'el-icon-s-flag',
        '自定义': 'el-icon-s-tools',
        '系统': 'el-icon-s-platform',
        '内置': 'el-icon-s-home',
        '外部': 'el-icon-link',
        '内部': 'el-icon-s-home',
        '公开': 'el-icon-view',
        '私有': 'el-icon-lock',
        '共享': 'el-icon-share',
        '独占': 'el-icon-lock',
        '只读': 'el-icon-view',
        '读写': 'el-icon-edit',
        '执行': 'el-icon-video-play',
        '调用': 'el-icon-phone',
        '触发': 'el-icon-aim',
        '响应': 'el-icon-chat-line-round',
        '回调': 'el-icon-refresh-left',
        '轮询': 'el-icon-refresh',
        '推送通知': 'el-icon-bell',
        '订阅': 'el-icon-star-on',
        '取消订阅': 'el-icon-star-off',
        '关注': 'el-icon-star-on',
        '取消关注': 'el-icon-star-off',
        '点赞': 'el-icon-star-on',
        '取消点赞': 'el-icon-star-off',
        '踩': 'el-icon-bottom',
        '举报': 'el-icon-warning',
        '拉黑': 'el-icon-close',
        '屏蔽': 'el-icon-turn-off',
        '过滤': 'el-icon-s-operation',
        '敏感词': 'el-icon-warning',
        '违规': 'el-icon-warning',
        '封禁': 'el-icon-lock',
        '解封': 'el-icon-unlock',
        '限制': 'el-icon-warning-outline',
        '解除限制': 'el-icon-success',
        '风控': 'el-icon-warning',
        '安全检测': 'el-icon-s-check',
        '漏洞': 'el-icon-warning',
        '修复': 'el-icon-s-tools',
        '补丁': 'el-icon-document-add',
        '升级': 'el-icon-top',
        '降级': 'el-icon-bottom',
        '回滚': 'el-icon-refresh-left',
        '发布版本': 'el-icon-s-promotion',
        '灰度': 'el-icon-s-operation',
        'AB测试': 'el-icon-s-data',
        '实验': 'el-icon-s-data',
        '测试': 'el-icon-s-check',
        '调试': 'el-icon-s-tools',
        '日志查看': 'el-icon-notebook-2',
        '错误日志': 'el-icon-warning',
        '访问日志': 'el-icon-view',
        '操作日志': 'el-icon-mouse',
        '登录日志': 'el-icon-user',
        '安全日志': 'el-icon-lock',
        '性能监控': 'el-icon-odometer',
        '资源监控': 'el-icon-s-data',
        '服务监控': 'el-icon-s-platform',
        '接口监控': 'el-icon-connection',
        '数据库监控': 'el-icon-s-data',
        '缓存监控': 'el-icon-s-data',
        '队列监控': 'el-icon-s-operation',
        '任务监控': 'el-icon-s-claim',
        '定时任务': 'el-icon-alarm-clock',
        '异步任务': 'el-icon-loading',
        '消息队列': 'el-icon-s-operation',
        '事件总线': 'el-icon-s-operation',
        '钩子': 'el-icon-link',
        '拦截器': 'el-icon-s-operation',
        '过滤器': 'el-icon-s-operation',
        '中间件': 'el-icon-s-operation',
        '网关': 'el-icon-s-platform',
        '代理服务': 'el-icon-s-platform',
        '负载均衡': 'el-icon-s-operation',
        '集群': 'el-icon-s-grid',
        '节点': 'el-icon-s-data',
        '实例': 'el-icon-s-data',
        '容器': 'el-icon-box',
        '镜像': 'el-icon-picture',
        '部署': 'el-icon-s-promotion',
        '构建': 'el-icon-s-tools',
        '编译': 'el-icon-s-tools',
        '打包': 'el-icon-box',
        '发布部署': 'el-icon-s-promotion',
        '持续集成': 'el-icon-refresh',
        '持续部署': 'el-icon-s-promotion',
        '版本控制': 'el-icon-s-operation',
        '代码仓库': 'el-icon-folder',
        '分支': 'el-icon-s-operation',
        '合并请求': 'el-icon-s-unfold',
        '代码审查': 'el-icon-view',
        '代码质量': 'el-icon-s-check',
        '单元测试': 'el-icon-s-check',
        '集成测试': 'el-icon-s-check',
        '压力测试': 'el-icon-s-data',
        '性能测试': 'el-icon-odometer',
        '安全测试': 'el-icon-lock',
        '渗透测试': 'el-icon-aim',
        '漏洞扫描': 'el-icon-search',
        '代码扫描': 'el-icon-search',
        '依赖检查': 'el-icon-link',
        '许可证': 'el-icon-document-checked',
        '开源协议': 'el-icon-document',
        '版权': 'el-icon-document-checked',
        '商标': 'el-icon-s-flag',
        '专利': 'el-icon-document-checked',
        '知识产权': 'el-icon-document-checked',
        '法律': 'el-icon-document',
        '合规': 'el-icon-s-check',
        '审计': 'el-icon-s-check',
        '内审': 'el-icon-s-check',
        '外审': 'el-icon-s-check',
        '整改': 'el-icon-s-tools',
        '改进': 'el-icon-top-right',
        '优化': 'el-icon-s-tools',
        '重构': 'el-icon-s-tools',
        '迭代': 'el-icon-refresh',
        '里程碑': 'el-icon-s-flag',
        '目标': 'el-icon-aim',
        'KPI': 'el-icon-s-data',
        'OKR': 'el-icon-s-data',
        '绩效': 'el-icon-s-data',
        '考核': 'el-icon-s-check',
        '评估': 'el-icon-s-check',
        '评分': 'el-icon-star-on',
        '打分': 'el-icon-star-on',
        '排名': 'el-icon-s-data',
        '等级评定': 'el-icon-medal',
        '晋升': 'el-icon-top',
        '降职': 'el-icon-bottom',
        '调岗': 'el-icon-sort',
        '离职': 'el-icon-switch-button',
        '入职': 'el-icon-user-solid',
        '转正': 'el-icon-success',
        '试用': 'el-icon-time',
        '实习': 'el-icon-school',
        '培训': 'el-icon-reading',
        '学习': 'el-icon-reading',
        '课程': 'el-icon-notebook-1',
        '教程': 'el-icon-notebook-2',
        '视频': 'el-icon-video-camera',
        '音频': 'el-icon-headset',
        '直播': 'el-icon-video-camera-solid',
        '录播': 'el-icon-video-camera',
        '回放': 'el-icon-video-play',
        '下载视频': 'el-icon-download',
        '缓存视频': 'el-icon-folder-add',
        '离线': 'el-icon-connection',
        '在线': 'el-icon-s-platform',
        '连接': 'el-icon-connection',
        '断开': 'el-icon-connection',
        '重连': 'el-icon-refresh',
        '心跳': 'el-icon-s-data',
        '超时': 'el-icon-time',
        '重试': 'el-icon-refresh',
        '失败': 'el-icon-error',
        '成功状态': 'el-icon-success',
        '等待': 'el-icon-loading',
        '排队': 'el-icon-s-operation',
        '处理中': 'el-icon-loading',
        '已处理': 'el-icon-success',
        '未处理': 'el-icon-warning',
        '已发送': 'el-icon-success',
        '未发送': 'el-icon-warning',
        '已接收': 'el-icon-success',
        '未接收': 'el-icon-warning',
        '已确认': 'el-icon-success',
        '未确认': 'el-icon-warning',
        '已支付': 'el-icon-success',
        '未支付': 'el-icon-warning',
        '已退款': 'el-icon-success',
        '退款中': 'el-icon-loading',
        '已取消': 'el-icon-close',
        '已关闭': 'el-icon-close',
        '已过期': 'el-icon-time',
        '即将过期': 'el-icon-warning',
        '永久有效': 'el-icon-success',
        '有效期': 'el-icon-time',
        '生效时间': 'el-icon-time',
        '失效时间': 'el-icon-time',
        '创建时间': 'el-icon-time',
        '更新时间': 'el-icon-time',
        '删除时间': 'el-icon-time',
        '最后登录': 'el-icon-time',
        '最后操作': 'el-icon-time',
        '最后修改': 'el-icon-time',
        '创建人': 'el-icon-user',
        '修改人': 'el-icon-user',
        '删除人': 'el-icon-user',
        '负责人': 'el-icon-user',
        '经办人': 'el-icon-user',
        '审批人': 'el-icon-user',
        '抄送人': 'el-icon-user',
        '接收人': 'el-icon-user',
        '发送人': 'el-icon-user',
        '创建者': 'el-icon-user',
        '管理员': 'el-icon-s-custom',
        '超级管理员': 'el-icon-s-custom',
        '普通用户': 'el-icon-user',
        '访客': 'el-icon-user',
        '游客': 'el-icon-user',
        'VIP': 'el-icon-star-on',
        'SVIP': 'el-icon-star-on',
        '黄金会员': 'el-icon-medal',
        '白金会员': 'el-icon-medal-1',
        '钻石会员': 'el-icon-trophy',
        '青铜': 'el-icon-medal',
        '白银': 'el-icon-medal',
        '黄金': 'el-icon-medal',
        '铂金': 'el-icon-medal-1',
        '钻石': 'el-icon-trophy',
        '王者': 'el-icon-trophy',
        '大师': 'el-icon-trophy',
        '宗师': 'el-icon-trophy',
        '传说': 'el-icon-trophy',
        '荣耀': 'el-icon-trophy',
        '至尊': 'el-icon-trophy',
        '无敌': 'el-icon-trophy',
        '最强': 'el-icon-trophy',
        '第一': 'el-icon-trophy',
        '冠军': 'el-icon-trophy',
        '亚军': 'el-icon-medal-1',
        '季军': 'el-icon-medal',
        '前三': 'el-icon-medal',
        '前十': 'el-icon-s-data',
        '百强': 'el-icon-s-data',
        '千强': 'el-icon-s-data',
        '万强': 'el-icon-s-data',
      }
      
      // 遍历匹配关键词
      for (const [keyword, icon] of Object.entries(keywordIconMap)) {
        if (name.includes(keyword)) {
          return icon
        }
      }
      
      // 如果没有匹配到关键词，根据菜单ID生成不同的图标
      const defaultIcons = [
        'el-icon-document',
        'el-icon-folder',
        'el-icon-files',
        'el-icon-collection',
        'el-icon-reading',
        'el-icon-notebook-1',
        'el-icon-notebook-2',
        'el-icon-tickets',
        'el-icon-postcard',
        'el-icon-document-copy',
        'el-icon-document-checked',
        'el-icon-document-add',
        'el-icon-document-remove',
        'el-icon-s-claim',
        'el-icon-s-order',
        'el-icon-s-management',
        'el-icon-s-ticket',
        'el-icon-s-release',
        'el-icon-s-comment',
        'el-icon-s-flag',
      ]
      
      // 使用菜单ID作为索引，确保同一菜单始终显示相同图标
      const index = (child.id || 0) % defaultIcons.length
      return defaultIcons[index]
    },

    /**
     * 菜单激活回调
     */
    menuSelect(index) {
      // 菜单ID到路由路径的映射（用于后端菜单path为空的情况）
      const menuPathMap = {
        // 数据分析模块
        130: '/dataOverview',
        131: '/vehicleAnalysis',
        132: '/orderAnalysis',
        133: '/driverAnalysis',
        134: '/revenueAnalysis',
        // 渠道管理模块
        201: '/channelAudit',
        202: '/channelList',
        203: '/channelCommission',
        204: '/channelWithdrawal',
        205: '/channelOrder',
        206: '/channelFinancial',
      }
      
      // 使用 find 代替 forEach 提高性能
      const findPath = (menus) => {
        for (const item of menus) {
          if (item.id == Number(index)) {
            return item.path
          }
          if (item.children && item.children.length > 0) {
            for (const child of item.children) {
              if (child.id == Number(index)) {
                return child.path
              }
            }
          }
        }
        return null
      }
      
      let path = findPath(this.originalMenu)
      
      // 如果后端返回的path为空，使用前端映射
      if (!path && menuPathMap[Number(index)]) {
        path = menuPathMap[Number(index)]
      }
      
      if (path) {
        this.$router.push(path)
        this.defaultActive = index
        sessionStorage.setItem("menuActive", index)
        
        // 搜索后选中菜单项时清除搜索
        if (this.searchText) {
          this.clearSearch()
        }
      }
    }
  }
};
</script>

<style lang="less">
#frame-menu {
  display: flex;
  flex-direction: column;
  width: 220px;
  height: 100%;
  background: linear-gradient(180deg, #1d1e23 0%, #2a2b30 100%);
  transition: width 0.3s ease;
  outline: none;
  overflow: hidden;
  
  // ============================================
  // 收起状态样式
  // ============================================
  &.menu-collapsed {
    width: 64px;
    
    .menu-collapse-btn {
      .collapse-btn-inner {
        justify-content: center;
        padding: 0;
        
        i {
          margin-right: 0;
        }
      }
    }
    
    .version-info {
      padding: 8px;
      justify-content: center;
      
      .version-badge {
        padding: 4px 8px;
        
        .el-icon-info {
          margin-right: 0;
        }
        
        .version-text {
          display: none;
        }
      }
      
      .version-detail {
        display: none;
      }
    }
    
    // 收起状态下的菜单项图标居中
    .el-menu-item {
      padding: 0 !important;
      text-align: center;
      
      i {
        margin-right: 0 !important;
      }
      
      span {
        display: none;
      }
    }
    
    .el-submenu {
      .el-submenu__title {
        padding: 0 !important;
        text-align: center;
        
        i {
          margin-right: 0 !important;
        }
        
        span {
          display: none;
        }
      }
    }
  }
  
  // ============================================
  // 折叠按钮
  // ============================================
  .menu-collapse-btn {
    flex-shrink: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
      
      .collapse-btn-inner {
        color: #fff;
        
        i {
          color: @color-primary;
        }
      }
    }
    
    .collapse-btn-inner {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 14px 20px;
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      transition: all 0.3s ease;
      
      i {
        font-size: 18px;
        margin-right: 8px;
        transition: color 0.3s ease;
      }
    }
  }

  // ============================================
  // 菜单容器
  // ============================================
  .menu-box {
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;

    // Firefox 滚动条样式
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) rgb(29, 30, 35);

    // WebKit 滚动条样式
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-track {
      background: rgb(29, 30, 35) !important;
    }
  }
  
  // ============================================
  // 错误状态
  // ============================================
  .menu-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: rgba(255, 255, 255, 0.7);
    text-align: center;
    
    i {
      font-size: 48px;
      color: @color-warning;
      margin-bottom: 16px;
    }
    
    p {
      margin-bottom: 12px;
      font-size: 14px;
    }
    
    .el-button {
      color: @color-primary;
      
      &:hover {
        color: lighten(@color-primary, 10%);
      }
    }
  }
  
  // ============================================
  // 空状态（搜索无结果）
  // ============================================
  .menu-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
    
    i {
      font-size: 36px;
      margin-bottom: 12px;
      opacity: 0.5;
    }
    
    p {
      margin-bottom: 12px;
      font-size: 14px;
    }
    
    .el-button {
      color: @color-primary;
    }
  }

  // ============================================
  // 版本号
  // ============================================
  .version {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    line-height: 36px;
    background: rgb(29, 30, 35);
    text-transform: lowercase;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    cursor: default;
    transition: color 0.3s ease;
    
    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  // ============================================
  // 菜单样式
  // ============================================
  .el-menu {
    border-right: none;
    
    // 收起状态下的菜单
    &.el-menu--collapse {
      width: 64px;
      
      .el-submenu__title,
      .el-menu-item {
        span {
          display: none;
        }
        
        i {
          margin-right: 0;
        }
      }
    }

    .el-menu-item {
      color: rgba(255, 255, 255, 0.7);
      transition: all 0.3s ease;
      height: 44px;
      line-height: 44px;
      
      i {
        color: rgba(255, 255, 255, 0.7);
        margin-right: 8px;
        transition: all 0.3s ease;
      }
      
      &:hover {
        background-color: rgba(63, 64, 69, 0.5) !important;
        color: #fff;
        
        i {
          color: #fff;
          transform: scale(1.1);
        }
      }
      
      &.is-active {
        background-color: rgba(43, 119, 255, 0.2) !important;
        color: #fff;
        border-right: 3px solid @color-primary;
        
        i {
          color: @color-primary;
        }
      }
      
      // 搜索高亮
      &.search-highlight {
        background-color: rgba(43, 119, 255, 0.15) !important;
        
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 60%;
          background: @color-primary;
          border-radius: 0 2px 2px 0;
        }
      }
    }

    .el-submenu {
      .el-submenu__title {
        height: 44px;
        line-height: 44px;
        transition: all 0.3s ease;
        
        span, i {
          color: rgba(255, 255, 255, 0.7);
          transition: color 0.3s ease;
        }
        
        .iconfont {
          margin-right: 8px;
        }
        
        &:hover {
          background-color: rgba(63, 64, 69, 0.5) !important;
          
          span, i {
            color: #fff;
          }
        }
      }
      
      .el-menu-item {
        padding-left: 52px !important;
        min-width: auto;
        position: relative;
      }

      &.is-opened {
        .el-submenu__title {
          background-color: rgba(63, 64, 69, 0.3) !important;
        }
      }
    }
  }
}

// ============================================
// 版本号显示区域
// ============================================
.version-info {
  padding: 12px 15px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
    
    .version-badge {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(43, 119, 255, 0.3);
    }
  }
  
  .version-badge {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    background: linear-gradient(135deg, rgba(43, 119, 255, 0.2) 0%, rgba(43, 119, 255, 0.1) 100%);
    border: 1px solid rgba(43, 119, 255, 0.3);
    border-radius: 16px;
    transition: all 0.3s ease;
    
    .el-icon-info {
      font-size: 14px;
      color: @color-primary;
      margin-right: 6px;
    }
    
    .version-text {
      font-size: 13px;
      font-weight: 600;
      color: #fff;
      letter-spacing: 0.5px;
    }
    
    // 开发环境标识
    &.is-dev {
      background: linear-gradient(135deg, rgba(250, 173, 20, 0.2) 0%, rgba(250, 173, 20, 0.1) 100%);
      border-color: rgba(250, 173, 20, 0.3);
      
      .el-icon-info {
        color: #faad14;
      }
      
      .version-text {
        &::after {
          content: ' DEV';
          font-size: 10px;
          color: #faad14;
          margin-left: 4px;
          font-weight: 500;
        }
      }
    }
  }
  
  .version-detail {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    cursor: help;
    
    &:hover {
      color: rgba(255, 255, 255, 0.7);
    }
  }
}

// ============================================
// 收起状态下的子菜单弹出样式
// ============================================
.collapsed-submenu-popper {
  .el-menu {
    background-color: #1d1e23 !important;
    min-width: 160px;
    
    .el-menu-item {
      padding-left: 20px !important;
      color: rgba(255, 255, 255, 0.7);
      
      &:hover {
        background-color: rgba(63, 64, 69, 0.5) !important;
        color: #fff;
      }
      
      &.is-active {
        background-color: rgba(43, 119, 255, 0.2) !important;
        color: #fff;
      }
    }
  }
}
</style>
